/**
 * createInvite — RPC contract for the createInvite callable.
 *
 * Cross-process contract between the web member-management seam (not yet
 * built) and `firebase/src/invites/createInvite.api.ts` (onCall
 * handler). Both ends parse with `.strict()` — an extra key fails the whole
 *
 * Fields justified:
 *   email        — the invitee's address; drives sendOTP/verifyOTP admission
 *                   once the invite lands, and the delivery address for the
 *                   invitation email.
 *   displayName  — the repo's single canonical identity field (not
 *                   momo-starter's firstName/lastName fold) — member-list
 *                   display and the email greeting only; the invitee's own
 *                   displayName is collected fresh at first-sign-in consent.
 *   roleId       — FK to the org's role catalog; the role granted on
 *                   redemption. The handler rejects an absent role and one
 *                   holding a permission the caller's own role lacks.
 *   scope        — reuses Invite's own `GrantScopeSchema` (discriminated
 *                   union over all-events/events) so the invite record and
 *                   its creation request never drift.
 *   locale       — the UI language ('de'/'en') the inviting admin is
 *                   currently viewing; the invitation email renders in this
 *                   language. Enum-closed so an untrusted/malformed value
 *                   fails the whole-request parse. Absent → 'de', matching
 *                   SendOTPInput's default.
 *
 * Output:
 *   token        — the minted invite token (server-generated), doc id of
 *                   `workspaces/{orgId}/invites/{token}`.
 *
 * Excluded:
 *   invitedBy / status / createdAt / expiresAt — server-stamped; the caller
 *                   supplies only what it authors, the handler mints the rest.
 *
 * Organization context is explicit in the request. The server validates
 * membership or possession of the scoped access token independently.
 */
import { LocaleEnum } from "@momo/i18n";
import { z } from "zod";
import { GrantScopeSchema } from "./grant.entity.ts";
import { Id } from "./workspace.entity.ts";

export const CreateInviteInput = z
	.object({
		orgId: Id,
		email: z.string().email().describe("the invitee's delivery address"),
		displayName: z
			.string()
			.min(1)
			.describe(
				"the invitee’s name, for the member-list row and email greeting",
			),
		roleId: z
			.string()
			.min(1)
			.describe(
				"the role to grant on redemption; must exist in the org catalog",
			),
		scope: GrantScopeSchema,
		locale: LocaleEnum.nullish(),
	})
	.strict();
export type CreateInviteInput = z.infer<typeof CreateInviteInput>;

export const CreateInviteOutput = z
	.object({
		token: z
			.string()
			.min(1)
			.describe(
				"the minted invite token — doc id of workspaces/{orgId}/invites/{token}",
			),
	})
	.strict();
export type CreateInviteOutput = z.infer<typeof CreateInviteOutput>;
